import { Dtos, pluralize } from "@inrev/common";
import { type ProCon, relatedProConId } from "@portal/api/decisioning";
import { useState } from "react";
import { ConfirmationModal } from "../../../../layout/ConfirmationModal";
import { cn } from "../../../../lib/utils";
import { Icon, Text } from "../../../../primitives";
import { useDecisioningData } from "../DecisioningDataContext";
import {
	JUMP_HIGHLIGHT_CLASSES,
	useDecisioningPanel,
	useJumpTargetProps,
} from "../DecisioningPanelContext";
import { FactorCardDetails } from "./FactorCardDetails";
import { FactorCardMenu } from "./FactorCardMenu";
import { useFactorsBoard } from "./FactorsBoardContext";
import { RelationLink } from "./RelationLink";

export type FactorCardProps = {
	proCon: ProCon;
	/** Drag attributes and listeners for the grip from `useSortable`. Omit to hide the grip. */
	dragHandleProps?: Record<string, unknown>;
	isDragging?: boolean;
	/** The grip stays focusable but cannot drag; its label says why. */
	dragDisabled?: boolean;
	/** Draws the drop line above or below this card while another card is dragged over it. */
	dropIndicator?: "before" | "after";
};

export const factorCardTestId = (id: ProCon["id"]) => `factor-card-${id}`;

export const FactorCard = ({
	proCon,
	dragHandleProps,
	isDragging = false,
	dragDisabled = false,
	dropIndicator,
}: FactorCardProps) => {
	const { relationshipsByProCon, dataNeededByProCon, proConsById } = useDecisioningData();
	const { isExpanded, setIsExpanded, jumpTo, openSheet } = useDecisioningPanel();
	const { hoveredId, setHoveredId, pendingIds, changeImportance, retire, remove } =
		useFactorsBoard();
	const jumpProps = useJumpTargetProps(proCon.id);
	const [confirmingDelete, setConfirmingDelete] = useState(false);

	const testId = factorCardTestId(proCon.id);
	const expanded = isExpanded(proCon.id);
	const pending = pendingIds.has(proCon.id);
	const relationships = relationshipsByProCon.get(proCon.id) ?? [];
	const openRequests = (dataNeededByProCon.get(proCon.id) ?? []).filter(
		(item) => item.status === "open",
	);
	const highlighted = jumpProps["data-highlighted"] || hoveredId === proCon.id || undefined;

	const handleImportanceChange = (importance: ProCon["importance"]) => {
		if (importance === proCon.importance) return;
		if (Dtos.Decisioning.requiresRationale(importance) && !proCon.detail?.trim()) {
			openSheet({ kind: "factor", mode: "edit", proConId: proCon.id, importance });
			return;
		}
		changeImportance(proCon, importance);
	};

	return (
		<div
			{...jumpProps}
			data-highlighted={highlighted}
			data-testid={testId}
			data-expanded={expanded || undefined}
			data-pending={pending || undefined}
			aria-busy={pending || undefined}
			className={cn(
				"group/item relative rounded-lg bg-surface-light py-2.5 pl-5 pr-3 transition-opacity",
				JUMP_HIGHLIGHT_CLASSES,
				isDragging && "opacity-40",
			)}
		>
			{dropIndicator && (
				<div
					aria-hidden
					data-testid="drop-indicator"
					data-position={dropIndicator}
					className={cn(
						"pointer-events-none absolute inset-x-0 z-10 h-0.5 rounded-full bg-brand-accent",
						dropIndicator === "before" ? "-top-[4px]" : "-bottom-[4px]",
					)}
				>
					<span className="absolute -left-[3px] -top-[2px] size-1.5 rounded-full bg-brand-accent" />
				</div>
			)}
			<div className="absolute left-1 top-2.5 flex w-3.5 justify-center">
				{dragHandleProps && !pending && (
					<button
						type="button"
						{...dragHandleProps}
						aria-label={
							dragDisabled
								? "Reordering unavailable while a factor is being added"
								: "Drag to reorder"
						}
						aria-disabled={dragDisabled || undefined}
						data-testid={`${testId}-drag-handle`}
						className="h-5 cursor-grab touch-none leading-5 text-tertiary opacity-0 outline-none transition-opacity duration-[120ms] hover:text-primary focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-bc-focus group-hover/item:opacity-100 active:cursor-grabbing"
					>
						<Text type="body" className="font-semibold tracking-[-3px] text-inherit" aria-hidden>
							⋮⋮
						</Text>
					</button>
				)}
			</div>
			<div className="flex min-w-0 flex-col gap-1.5">
				<button
					type="button"
					aria-expanded={expanded}
					data-testid={`${testId}-toggle`}
					className="flex w-full cursor-pointer flex-col items-stretch gap-1 rounded-sm pr-6 text-left outline-none focus-visible:ring-2 focus-visible:ring-bc-focus"
					onClick={() => setIsExpanded(proCon.id, !expanded)}
				>
					<Text
						type="body-strong"
						color="primary"
						className="block break-words [text-box-trim:none]"
					>
						{proCon.factor.name}
					</Text>
					{!expanded && proCon.detail && (
						<Text
							color="secondary"
							className="block text-[13px] font-normal leading-[18px]"
							truncate
							data-testid={`${testId}-rationale-preview`}
						>
							{proCon.detail}
						</Text>
					)}
				</button>
				{((!expanded && relationships.length > 0) || openRequests.length > 0) && (
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
						{!expanded &&
							relationships.map((relationship) => {
								const other = proConsById.get(relatedProConId(relationship, proCon.id));
								return other ? (
									<RelationLink
										key={relationship.id}
										relationship={relationship}
										viewerId={proCon.id}
										other={other}
										onHoverChange={setHoveredId}
										onJump={(id) => jumpTo({ tab: "factors", id })}
									/>
								) : null;
							})}
						{openRequests.length > 0 && (
							<span
								className="inline-flex items-center gap-1 text-tertiary"
								data-testid={`${testId}-open-items-hint`}
							>
								<Icon type="document-text" size="micro" />
								<Text type="caption" color="tertiary" className="font-normal">
									{pluralize(openRequests.length, "open item", "open items")}
								</Text>
							</span>
						)}
					</div>
				)}
				{expanded && (
					<FactorCardDetails
						proCon={proCon}
						relationships={relationships}
						openRequests={openRequests}
						testId={testId}
					/>
				)}
			</div>
			<div className="absolute right-1.5 top-2 flex justify-end">
				<FactorCardMenu
					factorName={proCon.factor.name}
					importance={proCon.importance}
					disabled={pending}
					testId={`${testId}-menu`}
					onEdit={() => openSheet({ kind: "factor", mode: "edit", proConId: proCon.id })}
					onConnect={() => openSheet({ kind: "connect", proConId: proCon.id })}
					onRequestData={() =>
						openSheet({ kind: "request", mode: "add", linkedProConId: proCon.id })
					}
					onRetire={() => retire(proCon)}
					onDelete={() => setConfirmingDelete(true)}
					onImportanceChange={handleImportanceChange}
				/>
			</div>
			{confirmingDelete && (
				<ConfirmationModal
					closeOnEscape
					message={`Delete ${proCon.factor.name}?`}
					subMessage="Use Retire if it was true once. Delete is for factors added in error."
					confirmButtonText="Delete"
					confirmButtonTestId={`${testId}-confirm-delete`}
					confirmationButtonColor="danger"
					onCancel={() => setConfirmingDelete(false)}
					onConfirm={() => {
						setConfirmingDelete(false);
						remove(proCon);
					}}
				/>
			)}
		</div>
	);
};
